import { useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import ArchiveIcon from "@mui/icons-material/Archive";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  deleteOrganization,
  listOrganizations,
} from "@/api/organizations";
import { listPinnedOrgs, pinOrg, unpinOrg } from "@/api/pinnedOrgs";
import type { Organization } from "@/api/types";
import { usePermission } from "@/auth/usePermission";
import { OrganizationCardGrid } from "@/components/OrganizationCardGrid";
import { OrganizationFormDrawer } from "@/components/OrganizationFormDrawer";
import { PageHeader } from "@/components/PageHeader";
import { StatusChip } from "@/components/StatusChip";
import { loadView, saveView, ViewToggle, type ListView } from "@/components/ViewToggle";

export function OrganizationsListPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const canCreate = usePermission("organization:create");
  const canUpdate = usePermission("organization:update");
  const canDelete = usePermission("organization:delete");

  const [q, setQ] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [editing, setEditing] = useState<Organization | "new" | null>(null);
  const [view, setView] = useState<ListView>(() => loadView("organizations"));
  const changeView = (v: ListView) => {
    setView(v);
    saveView("organizations", v);
  };

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["organizations", q, paginationModel.page, paginationModel.pageSize],
    queryFn: () =>
      listOrganizations({
        q: q || undefined,
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
      }),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => deleteOrganization(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["organizations"] }),
  });

  const pinned = useQuery({
    queryKey: ["pinned-organizations"],
    queryFn: listPinnedOrgs,
  });
  const pinnedSet = useMemo(
    () => new Set((pinned.data ?? []).map((p) => p.organization_id)),
    [pinned.data],
  );

  const togglePin = useMutation({
    mutationFn: (orgId: string) =>
      pinnedSet.has(orgId) ? unpinOrg(orgId).then(() => orgId) : pinOrg(orgId).then(() => orgId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pinned-organizations"] }),
  });

  const columns: GridColDef<Organization>[] = useMemo(
    () => [
      {
        field: "code",
        headerName: t("orgs.code"),
        minWidth: 140,
        flex: 1,
      },
      { field: "name", headerName: t("orgs.name"), minWidth: 200, flex: 2 },
      { field: "kind", headerName: t("orgs.kind"), minWidth: 110, flex: 1 },
      {
        field: "status",
        headerName: t("orgs.status"),
        minWidth: 130,
        flex: 1,
        renderCell: ({ row }) => <StatusChip value={row.status} />,
      },
      {
        field: "actions",
        headerName: t("orgs.actions"),
        width: 150,
        sortable: false,
        filterable: false,
        align: "right",
        headerAlign: "right",
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={pinnedSet.has(row.id) ? t("orgs.unpin") : t("orgs.pin")}>
              <IconButton
                size="small"
                onClick={() => togglePin.mutate(row.id)}
                color={pinnedSet.has(row.id) ? "warning" : "default"}
              >
                {pinnedSet.has(row.id) ? (
                  <StarIcon fontSize="small" />
                ) : (
                  <StarBorderIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            {canUpdate && (
              <Tooltip title={t("orgs.edit")}>
                <IconButton size="small" onClick={() => setEditing(row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && row.status !== "ARCHIVED" && (
              <Tooltip title={t("orgs.archive")}>
                <IconButton
                  size="small"
                  color="warning"
                  onClick={() => {
                    if (confirm(t("orgs.archiveConfirm"))) {
                      archiveMutation.mutate(row.id);
                    }
                  }}
                >
                  <ArchiveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [t, canUpdate, canDelete, archiveMutation, pinnedSet, togglePin],
  );

  // Pinned rows float to the top of the current page
  const sortedRows = useMemo(() => {
    const items = data?.items ?? [];
    return [...items].sort((a, b) => {
      const ap = pinnedSet.has(a.id);
      const bp = pinnedSet.has(b.id);
      if (ap === bp) return 0;
      return ap ? -1 : 1;
    });
  }, [data, pinnedSet]);

  return (
    <Box>
      <PageHeader
        helpTerm="organisation"
        title={t("orgs.title")}
        subtitle={t("orgs.subtitle")}
        actions={
          <>
            <ViewToggle view={view} onChange={changeView} />
            <Tooltip title={t("common.retry")}>
              <IconButton onClick={() => refetch()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setEditing("new")}
              >
                {t("orgs.create")}
              </Button>
            )}
          </>
        }
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder={t("orgs.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ minWidth: { xs: "100%", sm: 320 } }}
        />
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}

      {view === "table" ? (
        <Box sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={sortedRows}
            columns={columns}
            rowCount={data?.total ?? 0}
            loading={isLoading}
            getRowId={(row) => row.id}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            density="comfortable"
          />
        </Box>
      ) : (
        <OrganizationCardGrid
          rows={sortedRows}
          loading={isLoading}
          pinnedSet={pinnedSet}
          onTogglePin={(id) => togglePin.mutate(id)}
          onEdit={setEditing}
          onArchive={(id) => archiveMutation.mutate(id)}
          canUpdate={canUpdate}
          canDelete={canDelete}
        />
      )}

      <OrganizationFormDrawer
        open={editing !== null}
        organization={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          qc.invalidateQueries({ queryKey: ["organizations"] });
        }}
      />
    </Box>
  );
}
